<script setup>
import { computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useSlotManager } from 'dashboard/composables/useSlotManager';
import AgentModeConversationCard from './AgentModeConversationCard.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

defineProps({
  showConversationList: {
    type: Boolean,
    default: true,
  },
  activeConversationId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(['selectConversation']);

const store = useStore();
const { accountId } = useAccount();
const currentUserId = computed(() => store.getters.getCurrentUser?.id);

const slotManager = useSlotManager(accountId.value, currentUserId.value);
const mineChatsList = useMapGetter('getMineChats');
const chatListLoading = useMapGetter('getChatListLoadingStatus');

const conversations = computed(() =>
  mineChatsList.value({ assigneeType: 'me', status: 'open' })
);

const slottedConversations = computed(() => {
  const slots = slotManager.getSlots(conversations.value);
  return slots.map(slot => ({
    ...slot,
    conversation: slot.conversationId
      ? conversations.value.find(c => c.id === slot.conversationId)
      : null,
  }));
});

const handleConversationClick = conversation => {
  emit('selectConversation', conversation);
};

onMounted(async () => {
  await store.dispatch('updateChatListFilters', {
    assigneeType: 'me',
    status: 'open',
  });
  await store.dispatch('fetchAllConversations');
});
</script>

<template>
  <div
    v-show="showConversationList"
    class="flex flex-col flex-shrink-0 bg-n-background h-full w-full lg:min-w-[340px] lg:max-w-[340px]"
  >
    <!-- Inner card wrapper -->
    <div
      class="m-2 bg-white dark:bg-n-solid-1 border border-n-border-primary rounded-2xl p-3 flex flex-col flex-1 min-h-0 shadow-sm"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between pb-3 border-b border-n-weak"
      >
        <div>
          <h2 class="text-base font-semibold text-n-slate-12">
            {{ $t('AGENT_MODE.TITLE') }}
          </h2>
          <p class="text-xs text-n-slate-10">
            {{ $t('AGENT_MODE.SUBTITLE') }}
          </p>
        </div>
      </div>

      <!-- Slot list -->
      <div class="flex flex-col gap-1 flex-1 overflow-y-auto pt-3">
        <!-- Loading -->
        <div v-if="chatListLoading" class="flex justify-center my-4">
          <Spinner class="text-n-brand" />
        </div>

        <!-- Empty state -->
        <p
          v-else-if="!slottedConversations.length"
          class="p-4 text-sm font-medium text-center text-n-slate-10"
        >
          {{ $t('AGENT_MODE.NO_CONVERSATIONS') }}
        </p>

        <!-- Slots -->
        <template v-else>
          <div
            v-for="slot in slottedConversations"
            :key="slot.slotNumber"
            class="flex items-start gap-2"
          >
            <!-- Slot number badge -->
            <div
              class="flex-shrink-0 flex items-center justify-center w-6 h-6 mt-3 rounded-full bg-n-alpha-2 text-xs font-semibold text-n-slate-11"
            >
              {{ slot.slotNumber }}
            </div>
            <!-- Conversation item or empty slot -->
            <div class="flex-1 min-w-0">
              <AgentModeConversationCard
                v-if="slot.conversation"
                :chat="slot.conversation"
                :is-active="
                  slot.conversationId === Number(activeConversationId)
                "
                @click="handleConversationClick"
              />
              <div
                v-else
                class="flex items-center justify-center h-16 border border-dashed border-n-weak rounded-lg"
              >
                <span class="text-xs text-n-slate-10">{{
                  $t('AGENT_MODE.EMPTY_SLOT')
                }}</span>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
